import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import axios from 'axios';

export interface SortConfig {
  column: string;
  direction: 'ASC' | 'DESC';
}

export interface FilterConfig {
  column: string;
  term: string;
}

interface UseE55SamplesDataParams {
  fileId: number;
  page: number;
  size: number;
  enabled?: boolean;
  filters?: FilterConfig[];
  searchColumn?: string; // Deprecated: use filters instead
  searchTerm?: string; // Deprecated: use filters instead
  sorts?: SortConfig[];
  sort?: string; // Deprecated: use sorts instead
  direction?: string; // Deprecated: use sorts instead
  status?: string;
}

interface E55Sample {
  id: number;
  sampleId: string;
  sampleDate: string;
  sampleTime: string;
  sampleValue: number;
  sampleUnit: string;
  sampleType: string;
  sampleLocation: string;
  createdAt: string;
  updatedAt: string;
}

interface E55SamplesResponse {
  content: E55Sample[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export function useE55SamplesData({
  fileId,
  page,
  size,
  enabled = true,
  filters,
  searchColumn, // Legacy support
  searchTerm, // Legacy support
  sorts,
  sort, // Legacy support
  direction, // Legacy support
  status,
}: UseE55SamplesDataParams) {
  // Combine filters array with legacy searchColumn/searchTerm for backward compatibility
  const allFilters = useMemo(() => {
    const filterList: FilterConfig[] = filters ? [...filters] : [];
    // Add legacy searchColumn/searchTerm if provided and not already in filters
    if (searchColumn && searchTerm && !filterList.some(f => f.column === searchColumn)) {
      filterList.push({ column: searchColumn, term: searchTerm });
    }
    return filterList;
  }, [filters, searchColumn, searchTerm]);

  // Combine sorts array with legacy sort/direction for backward compatibility
  const allSorts = useMemo(() => {
    const sortList: SortConfig[] = sorts ? [...sorts] : [];
    // Add legacy sort/direction if provided and not already in sorts
    if (sort && direction && !sortList.some(s => s.column === sort)) {
      sortList.push({ column: sort, direction: direction as 'ASC' | 'DESC' });
    }
    return sortList;
  }, [sorts, sort, direction]);

  return useQuery<E55SamplesResponse>({
    queryKey: ['e55Samples', fileId, page, size, allFilters, allSorts, status],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('e55FileId', String(fileId));
      params.append('page', String(page));
      params.append('size', String(size));
      
      // Add multiple sorting parameters if provided
      if (allSorts && allSorts.length > 0) {
        allSorts.forEach((sort) => {
          // Format for backend sorting: "fieldName,direction"
          params.append('sort', `${sort.column},${sort.direction}`);
        });
      }
      
      // Add multiple filter parameters if provided
      if (allFilters && allFilters.length > 0) {
        allFilters.forEach((filter) => {
          if (filter.column && filter.term) {
            params.append(filter.column, filter.term);
          }
        });
      }

      // Add status parameter if provided
      if (status && status !== 'all') {
        params.append('status', status);
      }

      const response = await axios.get(`/data-sample/e55?${params.toString()}`);
      return response.data;
    },
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    enabled: enabled && !!fileId,
    refetchOnMount: true,
    refetchOnWindowFocus: false
  });
}
